import { useNavigate, useSearchParams } from 'react-router-dom';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ImportReviewState, SplitwiseFile } from '@splitfiesta/core';
import { initialReviewState } from '@splitfiesta/core';
import {
  isCurrencySupported,
  useNamespace,
  useTranslation,
} from '@splitfiesta/i18n';
import {
  GROUP_EXPENSE_LIMIT_ERROR_KEYS,
  type SplitwiseImportRequestDto,
} from '@splitfiesta/shared';

import { withAuth } from '@/components/auth';
import { ImportConfirmStep } from '@/components/import/ImportConfirmStep';
import { ImportFileStep } from '@/components/import/ImportFileStep';
import { ImportProgressStep } from '@/components/import/ImportProgressStep';
import { ImportReviewStep } from '@/components/import/ImportReviewStep';
import { ImportSummaryStep } from '@/components/import/ImportSummaryStep';
import { ImportStepper } from '@/components/import/ui/ImportStepper';
import { AppLayout } from '@/components/layout/AppLayout';
import { Skeleton, SkeletonTitle } from '@/components/skeleton';

import { useCreateSplitwiseImportMutation } from '@/hooks/queries/useImports';

import { useAuthStore } from '@/stores/authStore';

type DraftStep = 'file' | 'summary' | 'review' | 'confirm';
type ImportStep = DraftStep | 'progress';

const STEPS: ImportStep[] = [
  'file',
  'summary',
  'review',
  'confirm',
  'progress',
];

interface ParsedFile {
  file: SplitwiseFile;
  fileName: string;
  currencyFallback: boolean;
}

function GroupImportPage() {
  const { t } = useTranslation();
  const importLoaded = useNamespace('import');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const createImport = useCreateSplitwiseImportMutation();
  const deviceCurrency =
    useAuthStore(state => state.user?.settings?.currency) ?? 'USD';

  const [draftStep, setDraftStep] = useState<DraftStep>('file');
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [review, setReview] = useState<ImportReviewState | null>(null);
  const [submitError, setSubmitError] = useState<{
    key: string;
    params?: Record<string, unknown>;
  } | null>(null);
  // Ticks the stepper's last step; the job itself lives in the progress step.
  const [jobDone, setJobDone] = useState(false);
  const handleJobDone = useCallback(() => setJobDone(true), []);

  // The job id lives in the URL, not in state: the progress step offers the
  // new group while the import is still running, and coming back from it has
  // to land on the running job rather than on an empty file picker.
  const jobId = searchParams.get('job');
  const step: ImportStep = jobId ? 'progress' : draftStep;

  // Each step replaces the button that was clicked to reach it, which would
  // otherwise drop keyboard and screen-reader users back on `<body>`.
  const stepRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Keyed on `importLoaded` too: the first step arrives when the skeleton
    // goes, not when the step changes.
    if (!importLoaded) return;
    stepRef.current?.focus();
  }, [step, importLoaded]);

  const handleParsed = useCallback(
    (file: SplitwiseFile, fileName: string) => {
      // Always rebuilt, never merged: a review carries one name per column of
      // the file it was built for.
      const initial = initialReviewState(
        file,
        fileName,
        deviceCurrency,
        isCurrencySupported
      );
      setParsed({ file, fileName, currencyFallback: initial.currencyFallback });
      setReview(initial.review);
      setDraftStep('summary');
    },
    [deviceCurrency]
  );

  const handleConfirm = useCallback(
    async (payload: SplitwiseImportRequestDto) => {
      setSubmitError(null);
      try {
        const id = await createImport.mutateAsync(payload);
        // Replaced rather than pushed: the job is running server-side, so Back
        // must leave the flow instead of offering to start it a second time.
        setSearchParams({ job: id }, { replace: true });
        setJobDone(false);
        // The draft has been accepted by the server; leaving it armed would
        // re-arm the confirm step the moment the job id left the URL.
        setParsed(null);
        setReview(null);
        setDraftStep('file');
      } catch (err) {
        // The user stays on confirm so they can retry without rebuilding the
        // review; the file is still parsed in memory.
        const refusal = err as {
          translationKey?: unknown;
          details?: { limit?: number };
        } | null;
        setSubmitError(
          refusal?.translationKey ===
            GROUP_EXPENSE_LIMIT_ERROR_KEYS.importOverFreeLimit
            ? {
                key: GROUP_EXPENSE_LIMIT_ERROR_KEYS.importOverFreeLimit,
                params: { limit: refusal.details?.limit },
              }
            : { key: 'import:errors.submitFailed' }
        );
      }
    },
    [createImport, setSearchParams]
  );

  if (!importLoaded) {
    return (
      <AppLayout>
        <div className="mx-auto w-full max-w-2xl space-y-6">
          <SkeletonTitle className="h-8 w-64" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl dark:text-gray-100">
            {t('import:entry.title')}
          </h1>
          {step === 'file' && (
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {t('import:entry.subtitle')}
            </p>
          )}
        </div>

        <ImportStepper
          steps={STEPS.map(key => ({ key, label: t(`import:steps.${key}`) }))}
          currentKey={step}
          completed={step === 'progress' && jobDone}
          ariaLabel={t('import:entry.title')}
        />

        <div
          ref={stepRef}
          tabIndex={-1}
          data-testid="import-step-region"
          className="space-y-6 outline-none"
        >
          {jobId ? (
            <ImportProgressStep
              jobId={jobId}
              onOpenGroup={groupId => navigate(`/groups/${groupId}`)}
              onDone={handleJobDone}
            />
          ) : (
            <>
              {draftStep === 'file' && (
                <ImportFileStep
                  onParsed={handleParsed}
                  fileName={parsed?.fileName}
                />
              )}

              {draftStep === 'summary' && parsed && (
                <ImportSummaryStep
                  file={parsed.file}
                  zeroRowAssignments={review?.zeroRowAssignments}
                  categoryOverrides={review?.categoryOverrides}
                  onBack={() => setDraftStep('file')}
                  onContinue={() => setDraftStep('review')}
                />
              )}

              {draftStep === 'review' && parsed && review && (
                <ImportReviewStep
                  file={parsed.file}
                  review={review}
                  currencyFallback={parsed.currencyFallback}
                  onChange={setReview}
                  onBack={() => setDraftStep('summary')}
                  onContinue={() => setDraftStep('confirm')}
                />
              )}

              {draftStep === 'confirm' && parsed && review && (
                <ImportConfirmStep
                  file={parsed.file}
                  review={review}
                  submitting={createImport.isPending}
                  error={submitError}
                  onBack={() => setDraftStep('review')}
                  onConfirm={handleConfirm}
                />
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default withAuth(GroupImportPage);
